import { snakeCase } from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { categoricalColorSchemes } from '@nivo/colors';
import { ResponsivePie } from '@nivo/pie';
import { PieChartQueryBuilderPlugin, useRunQuery } from '@sierralabs/query-ui';
import { parseTaskStatus } from '@sierralabs/workflow-core';
import ChartLegends from '../../common/charts/ChartLegends';
import SiteConfigChart from '../../config/SiteConfigChart';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { CoreEvent } from '@sierralabs/core-ui';
import { ChartItemsEvent, ChartType } from './ChartItemsDialogPlugin';
import { SiteComponentStackConfigurator } from './SiteComponentStackPlugin';
import ComponentRenderer from '../renderers/ComponentRenderer';
import styled from 'styled-components';
export const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
});
const StyledTooltip = styled.div `
  background: #fff;
  border-radius: 2px;
  padding: 5px;

  .main {
    display: flex;
    flex-direction: row;
    align-items: center;

    div {
      width: 15px;
      height: 15px;
      margin-right: 5px;
    }

    .value {
      font-weight: bold;
      margin: 0 0 0 5px;
    }
  }

  .percentage {
    margin-top: 5px;
    font-weight: 600;
    font-size: 14px;
  }
`;
export const SitePieChart = props => {
    var _a, _b, _c, _d, _e, _f;
    const component = useSubscribeComponentChanges(SitePieChartPlugin, props);
    const { isHover, hoverProps } = useSiteContext();
    const elementRef = useRef(null);
    const { queryData, runQuery } = useRunQuery();
    const { nivo } = categoricalColorSchemes;
    const query = (_a = component.props) === null || _a === void 0 ? void 0 : _a.query;
    const [enableArcLinkLabels, setArcLinkLabels] = useState(false);
    const pieChartParams = (_d = (_c = (_b = query === null || query === void 0 ? void 0 : query.config) === null || _b === void 0 ? void 0 : _b.pluginConfigs) === null || _c === void 0 ? void 0 : _c.find(plugin => plugin.type === PieChartQueryBuilderPlugin.type)) === null || _d === void 0 ? void 0 : _d.params;
    const withChartDialog = (_e = component.children) === null || _e === void 0 ? void 0 : _e.find(child => child.type === 'chart-items-dialog');
    const classes = ['site-pie-chart'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const getLabel = useCallback((value) => {
        return (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.forWorkflowStatus) && typeof value === 'string'
            ? parseTaskStatus(value)
            : value;
    }, [pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.forWorkflowStatus]);
    const getStatsPercentage = useCallback((count) => {
        const itemsTotal = (queryData || []).reduce((acc, item) => acc + item.total, 0);
        const percent = (count / itemsTotal) * 100;
        return isFinite(percent) ? formatter.format(percent) + '%' : 'N/A';
    }, [queryData]);
    const pieChartStats = useMemo(() => {
        if (!pieChartParams || !queryData)
            return [];
        // format field name to match with raw data
        const [alias, property] = (pieChartParams.field || '').split('.');
        const fieldName = alias.concat('_', snakeCase(property));
        return queryData.map((item, index) => {
            const value = [null, 'null'].includes(item[fieldName])
                ? 'null'
                : item[fieldName];
            const colorIndex = nivo.length === 0
                ? -1
                : index - Math.floor(index / nivo.length) * nivo.length;
            return {
                id: value,
                value: item.total,
                label: getLabel(value),
                color: nivo[colorIndex],
                fieldValue: value
            };
        });
    }, [pieChartParams, queryData, nivo, getLabel]);
    const handleItemClick = (value) => {
        CoreEvent.dispatch(ChartItemsEvent.OpenDialog, {
            id: component.id,
            conditions: [
                {
                    field: (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.field) || '',
                    value
                }
            ]
        });
    };
    const customTooltip = useCallback(({ datum }) => {
        return (React.createElement(StyledTooltip, null,
            React.createElement("div", { className: 'main' },
                React.createElement("div", { style: { background: datum.color || datum.data.color } }),
                React.createElement("p", { style: { margin: 0 } },
                    datum.id || datum.data.id,
                    ":"),
                React.createElement("p", { className: 'value' }, datum.value || datum.data.value)),
            !!(pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.showPercentage) && !enableArcLinkLabels && (React.createElement("div", { className: 'percentage' },
                React.createElement("p", { style: { margin: 0 } }, getStatsPercentage(Number(datum.value || datum.data.value)))))));
    }, [pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.showPercentage, queryData, enableArcLinkLabels]);
    useLayoutEffect(() => {
        if (!elementRef.current)
            return;
        const resizeObserver = new ResizeObserver(e => {
            setArcLinkLabels((e === null || e === void 0 ? void 0 : e[0].target.clientWidth) > 520);
        });
        resizeObserver.observe(elementRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);
    useEffect(() => {
        if (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.field) {
            (query === null || query === void 0 ? void 0 : query.config) && runQuery(query.config, true);
        }
    }, [query, query === null || query === void 0 ? void 0 : query.config, pieChartParams, runQuery]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SitePieChartPlugin }, props),
        React.createElement("div", Object.assign({ style: { padding: 16 }, className: classes.join(' ') }, hoverProps(component)),
            React.createElement("div", { ref: elementRef, style: {
                    height: (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.chartHeight) || 300
                } },
                React.createElement(ResponsivePie, { onClick: data => {
                        handleItemClick(data.id);
                    }, data: pieChartStats, margin: { top: 30, right: 50, bottom: 30, left: 50 }, colors: { datum: 'data.color' }, innerRadius: 0.5, padAngle: 0.7, activeOuterRadiusOffset: 8, cornerRadius: 3, borderWidth: 1, borderColor: { from: 'color', modifiers: [['darker', 0]] }, enableArcLinkLabels: enableArcLinkLabels, tooltip: customTooltip, arcLinkLabelsSkipAngle: 10, arcLinkLabel: e => {
                        const value = (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.showPercentage)
                            ? getStatsPercentage(e.value)
                            : e.value;
                        return `${getLabel(e.id)} (${value})`;
                    }, arcLinkLabelsTextOffset: 6, arcLinkLabelsTextColor: '#333333', arcLinkLabelsThickness: 1, arcLinkLabelsColor: { from: 'color' }, arcLabelsSkipAngle: 10, arcLabelsTextColor: '#333333', animate: true, onMouseEnter: (_data, event) => {
                        if (!!withChartDialog) {
                            event.target.style.cursor = 'pointer';
                        }
                    } })),
            (pieChartParams === null || pieChartParams === void 0 ? void 0 : pieChartParams.showLegends) && (React.createElement(ChartLegends, { stats: pieChartStats, onItemClick: stat => {
                    handleItemClick(stat.fieldValue);
                } }))), (_f = component.children) === null || _f === void 0 ? void 0 :
        _f.map(child => (React.createElement(ComponentRenderer, { key: child.id, component: child })))));
};
export const SitePieChartConfiguration = props => {
    return (React.createElement(React.Fragment, null,
        React.createElement(SiteConfigChart, { component: props.component, plugin: PieChartQueryBuilderPlugin }),
        React.createElement(SiteComponentStackConfigurator, { component: props.component })));
};
export const SitePieChartPlugin = {
    name: 'Pie Chart',
    type: ChartType.Pie,
    configurator: SitePieChartConfiguration,
    component: SitePieChart,
    initConfig: component => {
        if (!component.props)
            component.props = {};
        // add PieChart Data set on component query config during initialization
        component.props.query = {
            config: {
                queryType: 'select',
                pluginConfigs: [{ type: PieChartQueryBuilderPlugin.type, params: {} }]
            }
        };
        return component;
    },
    slots: [
        {
            name: 'Chart Items Dialog',
            type: 'chart-items-dialog',
            slot: 'dialog'
        }
    ]
};
//# sourceMappingURL=SitePieChartPlugin.js.map